import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useLocation } from "react-router-dom";

import type { Artifact } from "@/types/chat";
import type { CitationData } from "@/vendor/Citation";
import type { PdfPanelDetails } from "@/vendor/PdfViewerPanel";

// Combined UI state types
interface UIState {
  showWelcomeContent: boolean;
  // PDF viewer panel state
  isPdfPanelOpen: boolean;
  pdfPanelDetails: PdfPanelDetails | null;
  // Citation panel state
  isCitationPanelOpen: boolean;
  citationPanelData: CitationData | null;
  // Artifact panel state
  isArtifactPanelOpen: boolean;
  selectedArtifact: Artifact | null;
}

interface UIActions {
  setShowWelcomeContent: (show: boolean) => void;
  // PDF viewer panel actions
  openPdfPanel: (details: PdfPanelDetails) => void;
  closePdfPanel: () => void;
  // Citation panel actions
  openCitationPanel: (citation: CitationData) => void;
  closeCitationPanel: () => void;
  // Artifact panel actions
  openArtifactPanel: (artifact: Artifact) => void;
  closeArtifactPanel: () => void;
}

interface UIContextType {
  state: UIState;
  actions: UIActions;
}

// Selector type for preventing unnecessary re-renders
export interface WelcomeContentSelectors {
  showWelcomeContent: boolean;
  setShowWelcomeContent: (show: boolean) => void;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

interface UIProviderProps {
  children: React.ReactNode;
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
  // Initialize states
  const [showWelcomeContent, setShowWelcomeContent] = useState(true);
  const [isPdfPanelOpen, setIsPdfPanelOpen] = useState(false);
  const [pdfPanelDetails, setPdfPanelDetails] =
    useState<PdfPanelDetails | null>(null);
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState(false);
  const [citationPanelData, setCitationPanelData] =
    useState<CitationData | null>(null);
  const [isArtifactPanelOpen, setIsArtifactPanelOpen] = useState(false);
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(
    null
  );

  // Single-pane policy: opening one right-side pane synchronously closes the
  // other two. Keeping all three open flags here means each open handler can
  // tear the others down in one render — no cross-pane useEffects needed.
  const openPdfPanel = React.useCallback((details: PdfPanelDetails) => {
    setIsCitationPanelOpen(false);
    setCitationPanelData(null);
    setIsArtifactPanelOpen(false);
    setSelectedArtifact(null);
    setPdfPanelDetails(details);
    setIsPdfPanelOpen(true);
  }, []);

  const closePdfPanel = React.useCallback(() => {
    setIsPdfPanelOpen(false);
    setPdfPanelDetails(null);
  }, []);

  const openCitationPanel = React.useCallback((citation: CitationData) => {
    setIsPdfPanelOpen(false);
    setPdfPanelDetails(null);
    setIsArtifactPanelOpen(false);
    setSelectedArtifact(null);
    setCitationPanelData(citation);
    setIsCitationPanelOpen(true);
  }, []);

  const closeCitationPanel = React.useCallback(() => {
    setIsCitationPanelOpen(false);
    setCitationPanelData(null);
  }, []);

  const openArtifactPanel = React.useCallback((artifact: Artifact) => {
    setIsCitationPanelOpen(false);
    setCitationPanelData(null);
    setIsPdfPanelOpen(false);
    setPdfPanelDetails(null);
    setSelectedArtifact(artifact);
    setIsArtifactPanelOpen(true);
  }, []);

  const closeArtifactPanel = React.useCallback(() => {
    setIsArtifactPanelOpen(false);
    setSelectedArtifact(null);
  }, []);

  // Close every right-side pane when the user navigates between routes —
  // pane content is per-page and would otherwise leak across sessions.
  const location = useLocation();
  useEffect(() => {
    setIsArtifactPanelOpen(false);
    setSelectedArtifact(null);
    setIsPdfPanelOpen(false);
    setPdfPanelDetails(null);
    setIsCitationPanelOpen(false);
    setCitationPanelData(null);
  }, [location.pathname]);

  // Memoized actions to prevent unnecessary re-renders
  const actions = useMemo(
    (): UIActions => ({
      setShowWelcomeContent,
      // PDF viewer panel actions
      openPdfPanel,
      closePdfPanel,
      // Citation panel actions
      openCitationPanel,
      closeCitationPanel,
      // Artifact panel actions
      openArtifactPanel,
      closeArtifactPanel,
    }),
    [
      openPdfPanel,
      closePdfPanel,
      openCitationPanel,
      closeCitationPanel,
      openArtifactPanel,
      closeArtifactPanel,
    ]
  );

  // Memoized state to prevent unnecessary re-renders
  const state = useMemo(
    (): UIState => ({
      showWelcomeContent,
      isPdfPanelOpen,
      pdfPanelDetails,
      isCitationPanelOpen,
      citationPanelData,
      isArtifactPanelOpen,
      selectedArtifact,
    }),
    [
      showWelcomeContent,
      isPdfPanelOpen,
      pdfPanelDetails,
      isCitationPanelOpen,
      citationPanelData,
      isArtifactPanelOpen,
      selectedArtifact,
    ]
  );

  const value = useMemo(
    (): UIContextType => ({
      state,
      actions,
    }),
    [state, actions]
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

// Base hook for accessing the UI context
export const useUIContext = (): UIContextType => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error("useUIContext must be used within a UIProvider");
  }
  return context;
};

// Selector hook to prevent unnecessary re-renders
export const useWelcomeContent = (): WelcomeContentSelectors => {
  const { state, actions } = useUIContext();

  return useMemo(
    () => ({
      showWelcomeContent: state.showWelcomeContent,
      setShowWelcomeContent: actions.setShowWelcomeContent,
    }),
    [state.showWelcomeContent, actions.setShowWelcomeContent]
  );
};
